import { Link } from 'react-router-dom';
import AppStoreBadge from '../components/AppStoreBadge';

function BoltIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L21 12l-3.75 5.25m-10.5 0L3 12l3.75-5.25" />
    </svg>
  );
}

function BeakerIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.382-1.35 3.382H7.65c-2 0-2.582-2.15-1.35-3.382L7.5 14.7" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

const features = [
  {
    Icon: BoltIcon,
    title: 'Debug with AI',
    desc: 'Export build errors formatted for ChatGPT and Claude. Get fixes in seconds, not minutes.',
  },
  {
    Icon: ShieldIcon,
    title: 'Visual Dashboard',
    desc: 'See builds, tests, and code quality in a beautiful macOS app. No more terminal scrolling.',
  },
  {
    Icon: CodeIcon,
    title: 'One-Click Export',
    desc: 'Copy all errors, warnings, and context to your clipboard instantly. Paste anywhere.',
  },
  {
    Icon: BeakerIcon,
    title: 'Local & Private',
    desc: 'Everything runs on your Mac. Your code never leaves your device. 100% local-first.',
  },
];

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative px-6 pt-32 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-500/5 via-transparent to-transparent pointer-events-none" />
        
        <div className="relative mx-auto max-w-5xl text-center">
          <div className="animate-fade-in flex flex-col items-center">
            <img 
              src="/app-icon.png" 
              alt="SwiftDev Toolkit" 
              className="w-24 h-24 sm:w-32 sm:h-32 rounded-3xl shadow-2xl shadow-brand-500/30 mb-8"
            />
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl leading-tight px-4">
              From Build Errors to
              <br />
              <span className="text-gradient">AI Fixes in One Click</span>
            </h1>
          </div>
          
          <p className="mt-8 text-lg sm:text-xl leading-7 sm:leading-8 text-slate-300 max-w-3xl mx-auto px-4 animate-fade-in" style={{ animationDelay: '0.1s', opacity: 0 }}>
            Native macOS dashboard that turns xcodebuild chaos into AI-ready reports. Parse builds, tests, and SwiftLint violations — ready to paste into ChatGPT, Claude, or Cursor.
          </p>
          
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 animate-slide-up" style={{ animationDelay: '0.2s', opacity: 0 }}>
            <AppStoreBadge />
            <Link
              to="/features"
              className="group flex items-center gap-2 text-base font-semibold text-slate-300 hover:text-white transition-all duration-300 px-8 py-4 rounded-xl hover:bg-white/5"
            >
              Explore Features 
              <ArrowRightIcon />
            </Link>
          </div>
          
          <div className="mt-16 text-sm text-slate-500 animate-fade-in" style={{ animationDelay: '0.3s', opacity: 0 }}>
            macOS 14+ • Native SwiftUI • Keyboard-first
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 py-32 relative">
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-20 px-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Built for AI-assisted development</h2>
            <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-3xl mx-auto">Stop losing error messages in 5,000 lines of terminal output. Get structured reports you can actually use.</p>
          </div>
          
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f, idx) => {
              const colors = [
                { bg: 'from-brand-500/20 to-brand-600/10', text: 'text-brand-400' },
                { bg: 'from-accent-purple-500/20 to-accent-purple-600/10', text: 'text-accent-purple-400' },
                { bg: 'from-accent-emerald-500/20 to-accent-emerald-600/10', text: 'text-accent-emerald-400' },
                { bg: 'from-accent-amber-500/20 to-accent-amber-600/10', text: 'text-accent-amber-400' },
              ];
              const color = colors[idx % colors.length];
              
              return (
                <div
                  key={f.title}
                  className="group glass-card rounded-3xl p-8 hover:glass-elevated transition-all duration-500 hover:scale-[1.02] cursor-pointer animate-scale-in"
                  style={{ animationDelay: `${idx * 0.1}s`, opacity: 0 }}
                >
                  <div className={`mb-5 inline-flex rounded-2xl bg-gradient-to-br ${color.bg} p-4 ${color.text} group-hover:scale-110 transition-transform duration-300`}>
                    <f.Icon />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="px-6 py-20 relative border-t border-white/5">
        <div className="mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="glass-card rounded-3xl p-8">
              <div className="text-4xl font-bold text-brand-400 mb-2">100%</div>
              <div className="text-slate-400">Local-first</div>
              <div className="text-sm text-slate-500 mt-2">Your code never leaves your Mac</div>
            </div>
            <div className="glass-card rounded-3xl p-8">
              <div className="text-4xl font-bold text-accent-purple-400 mb-2">1-Click</div>
              <div className="text-slate-400">Export to AI</div>
              <div className="text-sm text-slate-500 mt-2">Copy everything to clipboard instantly</div>
            </div>
            <div className="glass-card rounded-3xl p-8">
              <div className="text-4xl font-bold text-accent-emerald-400 mb-2">Native</div>
              <div className="text-slate-400">macOS App</div>
              <div className="text-sm text-slate-500 mt-2">SwiftUI, keyboard shortcuts, dark mode</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-32 relative">
        <div className="mx-auto max-w-4xl">
          <div className="glass-elevated rounded-[2rem] p-16 text-center relative overflow-hidden">
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 to-transparent pointer-events-none" />
            
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Stop scrolling. Start shipping.</h2>
              <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto px-4">
                Join iOS developers who are debugging with AI in seconds, not minutes. Free download, native macOS 14+.
              </p>
              <div className="mt-10 flex justify-center">
                <AppStoreBadge />
              </div>
              <p className="mt-8 text-sm text-slate-500">
                No account required • No subscription for core features • 100% local-first
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
